/**
 * RCTI Verification Tab Component
 *
 * Allows admins to upload RCTI PDF files and verify totals against BigQuery data.
 * Supports drag & drop, displays results with status badges, and allows downloading reports.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
  Download,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useRctiVerification } from '../hooks/useRctiVerification';
import type { VerificationResult, VerificationStatus } from '../types/rctiVerification';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

interface RctiVerificationTabProps {
  companyId: string;
  gradientStyle?: React.CSSProperties;
  currentTheme?: { from: string; to: string };
}

interface FileTemplate {
  id: string;
  name: string;
  templateName?: string;
  aggregator?: string;
}

/**
 * Format currency with proper Australian format
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Get status badge styling and icon
 */
function getStatusBadge(status: VerificationStatus): {
  className: string;
  icon: React.ReactNode;
  label: string;
} {
  switch (status) {
    case 'MATCH':
      return {
        className: 'bg-green-500/20 text-green-400 border-green-500/30',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Match',
      };
    case 'MISMATCH':
      return {
        className: 'bg-red-500/20 text-red-400 border-red-500/30',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Mismatch',
      };
    case 'KNOWN_VARIANCE':
      return {
        className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        icon: <AlertTriangle className="w-4 h-4" />,
        label: 'Known Variance',
      };
    case 'MISSING':
      return {
        className: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        icon: <HelpCircle className="w-4 h-4" />,
        label: 'Missing',
      };
    case 'PARSE_ERROR':
      return {
        className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        icon: <AlertTriangle className="w-4 h-4" />,
        label: 'Parse Error',
      };
    default:
      return {
        className: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        icon: <HelpCircle className="w-4 h-4" />,
        label: 'Unknown',
      };
  }
}

const RctiVerificationTab: React.FC<RctiVerificationTabProps> = ({
  companyId,
  gradientStyle = { background: 'linear-gradient(135deg, #2563eb, #3b82f6)' },
  currentTheme = { from: '#2563eb', to: '#3b82f6' },
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [templates, setTemplates] = useState<FileTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [toleranceDollars, setToleranceDollars] = useState(1.0);

  const {
    isVerifying,
    error,
    results,
    summary,
    verifiedAt,
    verifyPdfs,
    clearResults,
  } = useRctiVerification();

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const getTemplates = httpsCallable(functions, 'getTemplates');
        // Exclude demo/sample templates — this view auto-selects templates[0] as
        // the default RCTI template, which must never be a parser-less demo.
        const result = await getTemplates({ excludeDemo: true }) as { data: { success: boolean; templates: FileTemplate[] } };

        if (result.data.success) {
          setTemplates(result.data.templates);
          // Auto-select first template if available
          if (result.data.templates.length > 0) {
            setSelectedTemplateId(result.data.templates[0].id);
          }
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
    };

    void fetchTemplates();
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const pdfFiles = Array.from(files).filter(
      (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length === 0) {
      alert('Please select PDF files only');
      return;
    }

    setSelectedFiles((prev) => [...prev, ...pdfFiles]);
  }, []);

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  // Remove a file from selection
  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Start verification
  const handleVerify = useCallback(async () => {
    if (selectedFiles.length === 0) {
      alert('Please select PDF files to verify');
      return;
    }

    if (!selectedTemplateId) {
      alert('Please select a template');
      return;
    }

    const toleranceCents = Math.round(toleranceDollars * 100);
    await verifyPdfs(companyId, selectedTemplateId, selectedFiles, toleranceCents);
  }, [companyId, selectedTemplateId, selectedFiles, toleranceDollars, verifyPdfs]);

  // Clear all
  const handleClear = useCallback(() => {
    setSelectedFiles([]);
    clearResults();
  }, [clearResults]);

  // Download report as JSON
  const handleDownloadReport = useCallback(() => {
    if (!results || results.length === 0) return;

    const report = {
      companyId,
      templateId: selectedTemplateId,
      verifiedAt,
      tolerance: toleranceDollars,
      summary,
      results,
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rcti-verification-${companyId}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [companyId, selectedTemplateId, verifiedAt, toleranceDollars, summary, results]);

  // Toggle result expansion
  const toggleResultExpansion = useCallback((fileName: string) => {
    setExpandedResults((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) {
        next.delete(fileName);
      } else {
        next.add(fileName);
      }
      return next;
    });
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <FileCheck className="w-5 h-5" style={{ color: currentTheme.from }} />
              RCTI Verification
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Verify RCTI PDF summary totals against BigQuery commission data
            </p>
          </div>
          {results.length > 0 && (
            <button
              onClick={handleDownloadReport}
              className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
          )}
        </div>

        {/* Configuration Row */}
        <div className="flex items-center gap-4 mb-6">
          {/* Template Selector */}
          <div className="flex-1">
            <label className="block text-sm text-slate-400 mb-1">Template</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              disabled={isVerifying}
            >
              <option value="">Select template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name || template.templateName}
                </option>
              ))}
            </select>
          </div>

          {/* Tolerance Input */}
          <div className="w-40">
            <label className="block text-sm text-slate-400 mb-1">Tolerance ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={toleranceDollars}
              onChange={(e) => setToleranceDollars(parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              disabled={isVerifying}
            />
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
            isDragOver
              ? 'border-blue-500 bg-blue-500/10'
              : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
          <Upload
            className="w-12 h-12 mx-auto mb-4"
            style={{ color: isDragOver ? currentTheme.from : '#64748b' }}
          />
          <p className="text-white font-medium mb-2">
            Drop RCTI PDF files here or click to upload
          </p>
          <p className="text-sm text-slate-400">
            Supports ENTO100 Summary RUN X Mon YY.pdf format
          </p>
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleClear}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 rounded-lg text-sm"
                >
                  <span className="text-white">{file.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="text-slate-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Verify Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleVerify}
            disabled={isVerifying || selectedFiles.length === 0 || !selectedTemplateId}
            className="px-6 py-2.5 text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2"
            style={gradientStyle}
          >
            {isVerifying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <FileCheck className="w-4 h-4" />
                Verify PDFs
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {summary && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <h4 className="text-lg font-semibold text-white mb-4">Verification Summary</h4>
          <div className="grid grid-cols-6 gap-4">
            <div className="text-center p-4 bg-slate-700/30 rounded-lg">
              <p className="text-3xl font-bold text-white">{summary.total}</p>
              <p className="text-sm text-slate-400">Total</p>
            </div>
            <div className="text-center p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-3xl font-bold text-green-400">{summary.matched}</p>
              <p className="text-sm text-green-400/70">Matched</p>
            </div>
            <div className="text-center p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-3xl font-bold text-red-400">{summary.mismatched}</p>
              <p className="text-sm text-red-400/70">Mismatched</p>
            </div>
            <div className="text-center p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-3xl font-bold text-blue-400">{summary.knownVariance}</p>
              <p className="text-sm text-blue-400/70">Known Variance</p>
            </div>
            <div className="text-center p-4 bg-gray-500/10 border border-gray-500/30 rounded-lg">
              <p className="text-3xl font-bold text-gray-400">{summary.missing}</p>
              <p className="text-sm text-gray-400/70">Missing</p>
            </div>
            <div className="text-center p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <p className="text-3xl font-bold text-orange-400">{summary.parseErrors}</p>
              <p className="text-sm text-orange-400/70">Parse Errors</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Tolerance: {formatCurrency(summary.toleranceDollars)} | Verified at:{' '}
            {verifiedAt ? new Date(verifiedAt).toLocaleString() : 'N/A'}
          </p>
        </div>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-6 border-b border-slate-700/50">
            <h4 className="text-lg font-semibold text-white">Verification Results</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700/50">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Run
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    PDF Net
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    BQ Net
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Difference
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {results.map((result) => {
                  const statusBadge = getStatusBadge(result.status);
                  const isExpanded = expandedResults.has(result.fileName);

                  return (
                    <React.Fragment key={result.fileName}>
                      <tr className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <p className="text-sm text-white">{result.periodMonth || '-'}</p>
                          <p className="text-xs text-slate-500">{result.fileName}</p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <p className="text-sm text-white">{result.runNumber || '-'}</p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <p className="text-sm text-white font-mono">
                            {formatCurrency(result.pdfNet)}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <p className="text-sm text-white font-mono">
                            {result.status === 'MISSING'
                              ? '-'
                              : formatCurrency(result.bqNet)}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <p
                            className={`text-sm font-mono ${
                              result.netDiff > toleranceDollars
                                ? 'text-red-400'
                                : 'text-green-400'
                            }`}
                          >
                            {result.status === 'MISSING'
                              ? '-'
                              : formatCurrency(result.netDiff)}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${statusBadge.className}`}
                          >
                            {statusBadge.icon}
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => toggleResultExpansion(result.fileName)}
                            className="p-1.5 text-slate-400 hover:text-white transition-colors rounded hover:bg-slate-700/50"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <tr className="bg-slate-900/50">
                          <td colSpan={7} className="px-4 py-4">
                            <div className="grid grid-cols-4 gap-6 text-sm">
                              {/* Upfront */}
                              <div>
                                <p className="text-slate-400 mb-1">Upfront Commission</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">PDF:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.pdfUpfront)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">BQ:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.bqUpfront)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between border-t border-slate-700/50 mt-1 pt-1">
                                  <span className="text-slate-500">Diff:</span>
                                  <span
                                    className={`font-mono ${
                                      result.upfrontDiff > toleranceDollars
                                        ? 'text-red-400'
                                        : 'text-green-400'
                                    }`}
                                  >
                                    {formatCurrency(result.upfrontDiff)}
                                  </span>
                                </div>
                              </div>

                              {/* Trail */}
                              <div>
                                <p className="text-slate-400 mb-1">Trail Commission</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">PDF:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.pdfTrail)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">BQ:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.bqTrail)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between border-t border-slate-700/50 mt-1 pt-1">
                                  <span className="text-slate-500">Diff:</span>
                                  <span
                                    className={`font-mono ${
                                      result.trailDiff > toleranceDollars
                                        ? 'text-red-400'
                                        : 'text-green-400'
                                    }`}
                                  >
                                    {formatCurrency(result.trailDiff)}
                                  </span>
                                </div>
                              </div>

                              {/* Broker Deductions */}
                              <div>
                                <p className="text-slate-400 mb-1">Broker Deductions</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">PDF:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.pdfBrokerDeductions)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">BQ:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.bqBrokerDeductions)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between border-t border-slate-700/50 mt-1 pt-1">
                                  <span className="text-slate-500">Diff:</span>
                                  <span
                                    className={`font-mono ${
                                      result.brokerDeductionsDiff > toleranceDollars
                                        ? 'text-red-400'
                                        : 'text-green-400'
                                    }`}
                                  >
                                    {formatCurrency(result.brokerDeductionsDiff)}
                                  </span>
                                </div>
                              </div>

                              {/* Referrer Deductions */}
                              <div>
                                <p className="text-slate-400 mb-1">
                                  Referrer Deductions
                                  {result.referrerDeductionVariance && (
                                    <span className="ml-2 text-xs text-blue-400">
                                      (Known Variance)
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">PDF:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.pdfReferrerDeductions)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">BQ:</span>
                                  <span className="text-white font-mono">
                                    {formatCurrency(result.bqReferrerDeductions)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between border-t border-slate-700/50 mt-1 pt-1">
                                  <span className="text-slate-500">Diff:</span>
                                  <span
                                    className={`font-mono ${
                                      result.referrerDeductionVariance
                                        ? 'text-blue-400'
                                        : result.referrerDeductionsDiff > toleranceDollars
                                        ? 'text-red-400'
                                        : 'text-green-400'
                                    }`}
                                  >
                                    {formatCurrency(result.referrerDeductionsDiff)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Parse Error Message */}
                            {result.parseError && (
                              <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm text-orange-400">
                                {result.parseError}
                              </div>
                            )}

                            {/* BQ Record Count */}
                            {result.bqRecordCount > 0 && (
                              <p className="mt-4 text-xs text-slate-500">
                                BigQuery records: {result.bqRecordCount.toLocaleString()}
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RctiVerificationTab;
